const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.felicitafragrances.com/api';

export const META_STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Search',
  'AddToCart',
  'AddToWishlist',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'Lead',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'Schedule',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
]);

const GA4_TO_META_STANDARD = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase',
  search: 'Search',
};

const GA4_TO_META_CUSTOM = {
  view_cart: 'ViewCart',
  view_item_list: 'ViewItemList',
  select_item: 'SelectItem',
  remove_from_cart: 'RemoveFromCart',
  add_shipping_info: 'AddShippingInfo',
};

const BLOCKED_NON_STANDARD_EVENTS = new Set([
  'scroll',
  'page_view',
  'scroll_depth',
  'ScrollDepth',
  'gtm.scroll',
  'gtm.click',
  'whatsapp_chat_open',
  'whatsapp_message_sent',
]);

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const waitForCookie = async (name, timeoutMs = 1500) => {
  const existing = getCookie(name);
  if (existing) return existing;
  if (typeof window === 'undefined') return null;

  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    const value = getCookie(name);
    if (value) return value;
  }
  return getCookie(name);
};

const buildUserData = async (userData = {}) => ({
  email: userData.email || null,
  phone: userData.phone || null,
  externalId: userData.externalId || null,
  fbc: userData.fbc || await waitForCookie('_fbc', 500),
  fbp: userData.fbp || await waitForCookie('_fbp', 1500),
  clientUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
});

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const formatMetaProduct = (product, quantity = 1) => {
  const id = product?.id || product?.sku || product?.item_id;
  if (!id) return null;

  const price = toNumber(product?.unitPrice ?? product?.price ?? product?.item_price, 0);
  const qty = Math.max(1, toNumber(quantity ?? product?.quantity, 1));

  return {
    id: String(id),
    quantity: qty,
    item_price: price,
  };
};

export const buildMetaCustomData = ({
  products = [],
  value = null,
  currency = 'TRY',
  contentType = 'product',
  orderId = null,
  searchString = null,
  numItems = null,
  extra = {},
} = {}) => {
  const contents = products
    .map((product) => formatMetaProduct(product, product?.quantity))
    .filter(Boolean);

  const contentIds = contents.map((item) => item.id);
  const computedValue = value != null
    ? toNumber(value, 0)
    : contents.reduce((sum, item) => sum + item.item_price * item.quantity, 0);

  const payload = {
    content_type: contentType,
    currency,
    value: Number(computedValue.toFixed(2)),
    ...extra,
  };

  if (contentIds.length > 0) {
    payload.content_ids = contentIds;
    payload.contents = contents;
    payload.num_items = numItems ?? contents.reduce((sum, item) => sum + item.quantity, 0);
  }

  if (orderId) {
    payload.order_id = String(orderId);
  }

  if (searchString) {
    payload.search_string = searchString;
  }

  return payload;
};

let metaPixelGuardInstalled = false;

export const installMetaPixelGuard = () => {
  if (typeof window === 'undefined' || metaPixelGuardInstalled) return;
  if (!window.fbq) return;

  const originalFbq = window.fbq;
  if (originalFbq.__metaGuarded) {
    metaPixelGuardInstalled = true;
    return;
  }

  const guardedFbq = function guardedFbq(...args) {
    const [method, eventName] = args;

    if (method === 'track' && typeof eventName === 'string') {
      if (
        GA4_TO_META_STANDARD[eventName]
        || GA4_TO_META_CUSTOM[eventName]
        || BLOCKED_NON_STANDARD_EVENTS.has(eventName)
      ) {
        return undefined;
      }

      if (!META_STANDARD_EVENTS.has(eventName)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[Meta Pixel] Blocked non-standard fbq("track", "${eventName}"). ` +
            'Use metaConversions helpers or fbq("trackCustom", ...).'
          );
        }
        return undefined;
      }
    }

    return originalFbq.apply(this, args);
  };

  guardedFbq.__metaGuarded = true;
  guardedFbq.callMethod = originalFbq.callMethod;
  guardedFbq.queue = originalFbq.queue;
  guardedFbq.loaded = originalFbq.loaded;
  guardedFbq.version = originalFbq.version;
  guardedFbq.push = originalFbq.push;

  window.fbq = guardedFbq;
  metaPixelGuardInstalled = true;
};

const postServerEvent = async (payload) => {
  try {
    await fetch(`${API_BASE_URL}/meta/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (error) {
    console.warn('Meta CAPI relay failed:', error);
  }
};

const dispatchMetaPixel = (method, eventName, customData, eventId) => {
  installMetaPixelGuard();

  if (typeof window === 'undefined' || !window.fbq) return;

  if (eventId) {
    window.fbq(method, eventName, customData, { eventID: eventId });
    return;
  }

  window.fbq(method, eventName, customData);
};

export const createEventId = (eventName, seed = null) => {
  if (seed) {
    return `${eventName.toLowerCase()}_${seed}`;
  }
  const rand = Math.random().toString(36).slice(2, 10);
  return `${eventName.toLowerCase()}_${Date.now()}_${rand}`;
};

export const trackMetaEvent = async ({
  eventName,
  eventId,
  eventSourceUrl,
  customData = {},
  userData = {},
  useCustom = false,
}) => {
  if (!eventName || !eventId) return;

  const pixelMethod = useCustom ? 'trackCustom' : 'track';
  dispatchMetaPixel(pixelMethod, eventName, customData, eventId);

  await postServerEvent({
    eventName,
    eventId,
    eventTime: Math.floor(Date.now() / 1000),
    eventSourceUrl: eventSourceUrl || (typeof window !== 'undefined' ? window.location.href : null),
    actionSource: 'website',
    customData,
    userData: await buildUserData(userData),
  });
};

export const trackMetaPageView = async (eventSourceUrl) => {
  const eventId = createEventId('PageView');
  await trackMetaEvent({
    eventName: 'PageView',
    eventId,
    eventSourceUrl,
    customData: {},
  });
};

export const trackMetaViewContent = async (product, userData = {}) => {
  if (!product) return;

  const eventId = createEventId('ViewContent', product.id || product.sku);
  await trackMetaEvent({
    eventName: 'ViewContent',
    eventId,
    customData: buildMetaCustomData({
      products: [product],
      value: product.price,
    }),
    userData,
  });
};

export const trackMetaAddToCart = async (product, userData = {}) => {
  if (!product) return;

  const quantity = product.quantity || 1;
  const eventId = createEventId('AddToCart', `${product.id}_${Date.now()}`);
  await trackMetaEvent({
    eventName: 'AddToCart',
    eventId,
    customData: buildMetaCustomData({
      products: [product],
      value: toNumber(product.price, 0) * quantity,
    }),
    userData,
  });
};

export const trackMetaViewCart = async (cartItems, value, userData = {}) => {
  if (!cartItems?.length) return;

  const eventId = createEventId('ViewCart');
  await trackMetaEvent({
    eventName: 'ViewCart',
    eventId,
    customData: buildMetaCustomData({
      products: cartItems,
      value,
    }),
    userData,
    useCustom: true,
  });
};

export const trackMetaViewItemList = async (items, listName = 'Product List') => {
  if (!items?.length) return;

  const eventId = createEventId('ViewItemList', listName.replace(/\s+/g, '_').toLowerCase());
  await trackMetaEvent({
    eventName: 'ViewItemList',
    eventId,
    customData: buildMetaCustomData({
      products: items.slice(0, 20),
      extra: { content_name: listName },
    }),
    useCustom: true,
  });
};

export const trackMetaInitiateCheckout = async (cartItems, value, userData = {}) => {
  if (!cartItems?.length) return;

  const eventId = createEventId('InitiateCheckout');
  await trackMetaEvent({
    eventName: 'InitiateCheckout',
    eventId,
    customData: buildMetaCustomData({
      products: cartItems,
      value,
    }),
    userData,
  });
};

export const trackMetaAddPaymentInfo = async (cartItems, value, userData = {}) => {
  if (!cartItems?.length) return;

  const eventId = createEventId('AddPaymentInfo');
  await trackMetaEvent({
    eventName: 'AddPaymentInfo',
    eventId,
    customData: buildMetaCustomData({
      products: cartItems,
      value,
    }),
    userData,
  });
};

export const trackMetaPurchase = async ({
  orderId,
  value,
  items = [],
  userData = {},
}) => {
  const eventId = createEventId('Purchase', orderId);
  await trackMetaEvent({
    eventName: 'Purchase',
    eventId,
    customData: buildMetaCustomData({
      products: items,
      value,
      orderId,
    }),
    userData,
  });
};

export const trackMetaAddShippingInfo = async (cartItems, value, shippingTier, userData = {}) => {
  if (!cartItems?.length) return;

  const eventId = createEventId('AddShippingInfo');
  await trackMetaEvent({
    eventName: 'AddShippingInfo',
    eventId,
    customData: buildMetaCustomData({
      products: cartItems,
      value,
      extra: { shipping_tier: shippingTier },
    }),
    userData,
    useCustom: true,
  });
};

export const trackMetaRemoveFromCart = async (product, value, userData = {}) => {
  if (!product) return;

  const eventId = createEventId('RemoveFromCart', product.id);
  await trackMetaEvent({
    eventName: 'RemoveFromCart',
    eventId,
    customData: buildMetaCustomData({
      products: [product],
      value,
    }),
    userData,
    useCustom: true,
  });
};

export const trackMetaSearch = async (searchString, userData = {}) => {
  if (!searchString) return;

  const eventId = createEventId('Search', searchString);
  await trackMetaEvent({
    eventName: 'Search',
    eventId,
    customData: {
      search_string: searchString,
      content_type: 'product',
    },
    userData,
  });
};

export const resolveMetaEventFromGa4 = (ga4EventName) => {
  if (GA4_TO_META_STANDARD[ga4EventName]) {
    return { eventName: GA4_TO_META_STANDARD[ga4EventName], useCustom: false };
  }
  if (GA4_TO_META_CUSTOM[ga4EventName]) {
    return { eventName: GA4_TO_META_CUSTOM[ga4EventName], useCustom: true };
  }
  return null;
};
